import type {ReactNode} from 'react';
import React from 'react';

import {Card, Heading, Icon, Text} from '@albert-io/atomic';

import type {QuestionModelV3, QuestionSetModelV1, SubjectModelV2} from '@albert-io/models';

import {QuestionWrapper, HeadingBar} from 'client/components/QuestionTypes/shared/ContentBlocks';

import {RESTRICTED_QUESTION_LABEL} from './teacherRestriction.utils';

interface NavigationProps {
  disabled: boolean;
  handleNavigation: () => void;
}

interface Props {
  actions?: ReactNode;
  activeQuestion: QuestionModelV3;
  activeQuestionSet: QuestionSetModelV1;
  activeSubject: SubjectModelV2;
  headingActions: ReactNode;
  navigateBackProps: NavigationProps | null;
  navigateNextProps: NavigationProps | null;
}

// ~480px: readable card measure for the two-section teacher copy; no spacing-token equivalent.
const LOCK_CARD_MAX_WIDTH = 'max-w-[480px]';

// ~560px: placeholder column width from the design (matches the student block); no spacing-token equivalent.
const SKELETON_MAX_WIDTH = 'max-w-[560px]';

// Decorative placeholder bars standing in for the hidden question content behind the lock card. The
// backend never sends the content, so these are static placeholders (not blurred real text); stable
// ids keep them out of the array-index-key rule. Widths vary so the bars read as prose.
const SKELETON_LINES = [
  'w-full',
  'w-full',
  'w-full',
  'w-[60%]',
  'w-full',
  'w-full',
  'w-full',
  'w-[70%]',
  'w-full',
  'w-full',
  'w-[50%]'
].map((width, index) => ({id: `skeleton-line-${index}`, width}));

/**
 * Metadata (title, difficulty, navigation) stays visible because the backend redacts only content
 * (stem/answer/explanation) so builder and reporting keep working. Copy stays neutral: a teacher
 * can't read which schools restricted it.
 *
 * The body grid-stacks the lock card over decorative skeleton bars in one grid cell (the hidden
 * content is never sent by the backend, so the bars are placeholders, not blurred real text);
 * stacking lets the cell grow with whichever is taller so the card can't be clipped. Mirrors the
 * student block panel (`BlockedForStudentQuestion`) — the structure is shared; only the copy differs.
 */
export default function BlockedForTeacherQuestion({
  activeQuestion,
  activeQuestionSet,
  activeSubject,
  actions,
  navigateBackProps,
  navigateNextProps,
  headingActions
}: Props) {
  return (
    <QuestionWrapper
      question={activeQuestion}
      questionSet={activeQuestionSet}
      subject={activeSubject}
      heading={<HeadingBar question={undefined} questionSet={undefined} showDifficulty />}
      modifyTitle={() => RESTRICTED_QUESTION_LABEL}
      navigateBackProps={navigateBackProps}
      navigateNextProps={navigateNextProps}
      headingActions={headingActions}
    >
      <div className='grid p-x6'>
        <div
          className={`col-start-1 row-start-1 mx-auto flex w-full flex-col gap-x2 ${SKELETON_MAX_WIDTH}`}
          aria-hidden='true'
        >
          {SKELETON_LINES.map((line) => (
            <div
              key={line.id}
              className={`h-x1-half rounded-full bg-background-subtle ${line.width}`}
            />
          ))}
        </div>
        <div className='col-start-1 row-start-1 flex items-center justify-center'>
          <Card
            roundness='large'
            shadow='default'
            paddingLevel={4}
            className={`w-full ${LOCK_CARD_MAX_WIDTH}`}
          >
            <div className='flex flex-col gap-x2'>
              <span className='inline-flex items-center justify-center self-start rounded-full bg-background-subtle p-x2'>
                <Icon
                  icon='lock'
                  iconStyle='solid'
                  color='secondary'
                  size='inherit'
                  className='text-xxl'
                />
              </span>
              {/* h2 (not h3): title above is h1, so h3 would skip a level and fail heading-order a11y. */}
              <Heading as='h2' size='s' className='m-x0!'>
                This question is restricted by your admin
              </Heading>
              <div className='flex flex-col gap-x2-half'>
                <div className='flex flex-col gap-x0-half'>
                  <Text bold>For you</Text>
                  <Text color='secondary'>
                    The question still appears in your assignments and reports, but you can&apos;t
                    see the content.
                  </Text>
                </div>
                <div className='flex flex-col gap-x0-half'>
                  <Text bold>For your students</Text>
                  <Text color='secondary'>
                    The question still appears in their assignments, but they can&apos;t see the
                    content or submit answers unless an admin authorizes the assignment.
                  </Text>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
      {actions || null}
    </QuestionWrapper>
  );
}
